import React, { useEffect, useState } from 'react';
import { useAllAccounts, useUserAccount } from '../hooks/useAccount';
import { useUser } from '../hooks/useUser';
import { Account, IFormulaToAccount, IRiskTolerance } from '../sp-types/accountTypes';
import { useAccountStore } from './Stores/accountStore';
import { useAdminModeStore } from './Stores/EditAdminStore';
import ButtonDesigned from './UI-Elements/Buttons/ButtonDesigned';
import { useAppSession } from '../contexts/AppSessionContext';
import { getAccountMaterials } from '../services/accountMaterialsService';
import { useMaterialsStore } from './Stores/MaterialsStore';
import { Material } from '../sp-types/materialType';
import { auth } from '../sp-firebase/firebase';
import { getAccount } from '../sp-firebase/accountFB';

const AccountSelector = () => {
  // Relevant For Admin Mode
  const [companyNames, setCompanyNames] = useState<any[]>([]);
  const { setAccount, setHasAccount, setIsAccountLoading, setFormulaToAccount } = useAccountStore();
  const { setMaterials } = useMaterialsStore();
  let idToCompanyNameMap = {};
  const { isAdminMode, setIsAdminMode } = useAdminModeStore();
  const { data: userData } = useUser();
  const { data: accountData, isLoading: isAccountDataLoading } = useUserAccount(!isAdminMode);
  const { user: sessionUser, firebaseReady } = useAppSession();
  
  // Derive isAdmin from user data (single source of truth)
  const isAdmin = userData?.isAdmin || false;

  //Updates account store
  useEffect(() => {
    console.log('[AccountSelector] accountData effect', { 
      hasAccountData: !!accountData?.account,
      isAdminMode,
      accountId: accountData?.account?.[1]
    });
    
    if (accountData?.account) {
      setAccount(accountData.account);
      setHasAccount(true);
    }
  }, [accountData]);

  // Fetch custom terms from Postgres for the current account
  useEffect(() => {
    if (!firebaseReady || !sessionUser?.accountId) return;
    
    const fetchCustomTerms = async () => {
      console.log('[AccountSelector] Fetching custom terms for account:', sessionUser.accountId);
      
      try {
        const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';
        const user = auth.currentUser;
        if (!user) {
          console.log('[AccountSelector] No Firebase auth user (unexpected — firebaseReady is true)');
          return;
        }
        
        const token = await user.getIdToken();
        const response = await fetch(`${API_URL}/api/v2/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          const pgAccount = data.accounts?.find((acc: any) => String(acc.id) === String(sessionUser.accountId));
          
          if (pgAccount) {
            console.log('[AccountSelector] ✅ Fetched custom terms from Postgres:', {
              accountId: sessionUser.accountId,
              demandTerm: pgAccount.demandTerm,
              supplyTerm: pgAccount.supplyTerm,
              hedgeTerm: pgAccount.hedgeTerm,
            });
            
            // Get current account from Firebase and merge custom terms
            const fbAccount = await getAccount(sessionUser.accountId);
            if (fbAccount) {
              fbAccount[0].demandTerm = pgAccount.demandTerm || 'Demand';
              fbAccount[0].supplyTerm = pgAccount.supplyTerm || 'Supply';
              fbAccount[0].hedgeTerm = pgAccount.hedgeTerm || 'Hedges';
              fbAccount[0].viewingHorizonMonths = pgAccount.viewingHorizonMonths || 12;
              fbAccount[0].baseCurrency = pgAccount.currency || 'USD';
              fbAccount[0].lastImportAt = pgAccount.lastImportAt || null;
              fbAccount[0].dataFreshnessThresholds = pgAccount.settings?.dataFreshnessThresholds || undefined;
              
              setAccount(fbAccount);
              console.log('[AccountSelector] ✅ Updated account store with custom terms');
            }
          }
        } else {
          console.error('[AccountSelector] Failed to fetch from Postgres API:', response.status);
        }
      } catch (error) {
        console.error('[AccountSelector] Failed to fetch custom terms:', error);
      }
    };
    
    fetchCustomTerms();
  }, [sessionUser?.accountId, firebaseReady]);

  // Load materials for current account from API and hydrate formulasToAccount
  useEffect(() => {
    if (!firebaseReady || !sessionUser?.accountId) return;

    const loadAccountMaterials = async () => {
      try {
        console.log('[AccountSelector] loadAccountMaterials effect', {
          accountId: sessionUser.accountId,
        });

        const apiMaterials = await getAccountMaterials(sessionUser.accountId);

        console.log('[AccountSelector] Received account materials from API', apiMaterials);

        // Fetch account metadata from Postgres API to get custom terms and baseCurrency
        try {
          const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';
          const user = auth.currentUser;
          if (user) {
            const token = await user.getIdToken();
            const accountResponse = await fetch(`${API_URL}/api/v2/accounts`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (accountResponse.ok) {
              const accountData = await accountResponse.json();
              const pgAccount = accountData.accounts?.find((acc: any) => String(acc.id) === String(sessionUser.accountId));
              
              if (pgAccount) {
                console.log('[AccountSelector] ✅ Fetched account metadata from Postgres:', {
                  demandTerm: pgAccount.demandTerm,
                  supplyTerm: pgAccount.supplyTerm,
                  hedgeTerm: pgAccount.hedgeTerm,
                  baseCurrency: pgAccount.currency,
                });
                
                // Update the existing account in store with Postgres metadata
                // Use Zustand's set to merge into existing state
                const { account: currentAccount } = useAccountStore.getState();
                useAccountStore.setState({
                  account: {
                    ...currentAccount,
                    demandTerm: pgAccount.demandTerm || 'Demand',
                    supplyTerm: pgAccount.supplyTerm || 'Supply',
                    hedgeTerm: pgAccount.hedgeTerm || 'Hedges',
                    viewingHorizonMonths: pgAccount.viewingHorizonMonths || 12,
                    baseCurrency: pgAccount.currency || 'USD',
                    visiblePolicies: pgAccount.settings?.visiblePolicies || undefined,
                    riskTolerance: pgAccount.riskTolerance || undefined,
                    lastImportAt: pgAccount.lastImportAt || null,
                    dataFreshnessThresholds: pgAccount.settings?.dataFreshnessThresholds || undefined,
                  }
                });
                console.log('[AccountSelector] ✅ Updated account in store with Postgres metadata');
              }
            }
          }
        } catch (error) {
          console.error('[AccountSelector] Failed to fetch account metadata from Postgres:', error);
        }

        // Fallback: account-level general thresholds → zeros
        const { account: acct } = useAccountStore.getState();
        const fallbackRT: IRiskTolerance = acct.riskTolerance || {
          dangerMinus: 0, warnMinus: 0, warnPlus: 0, dangerPlus: 0,
        };

        console.log('[AccountSelector] Account materials from API:', apiMaterials);
        
        const mapped: IFormulaToAccount[] = apiMaterials.map((m) => ({
          id: parseInt(m.formulaId, 10), // Numeric database ID for API calls
          formulaId: m.formulaCode, // String code for UI/charts/pricing
          displayName: m.formulaName,
          quoteSourceSymbol: m.quoteSourceSymbol,
          unit: m.unit,
          currency: m.quoteCurrency,
          currentPrice: m.currentPrice,
          terminals: m.tradingCalendar || undefined,
          RiskTolerance: m.riskTolerance || fallbackRT,
          RoutePriority: [],
          bufferStrategy: m.bufferStrategy || 0,
          logisticsStrategy: m.logisticsStrategy || undefined,
          isPercentStrategy: m.isPercentStrategy || false,
          covRatioStrategy: m.covRatioStrategy || undefined,
          covRatioIsPercent: m.covRatioIsPercent || false,
          isPriceWatchOnly: m.isPriceWatchOnly || false,
        }));
        
        // Also populate materialsMap with volatility data from API
        const materialsForStore: Material[] = apiMaterials.map((m) => ({
          id: m.formulaCode,
          name: m.formulaName,
          priceIndex: m.quoteSourceSymbol || '',
          currentPrice: m.currentPrice || 0,
          priceDate: null as any, // Not used from API
          unit: m.unit,
          currency: m.quoteCurrency || 'USD',
          terminals: m.tradingCalendar || [],
          contractType: m.contractType || undefined, // Contract type: 'material', 'ratio', 'FX'
          volatility: m.volatility || undefined,
          volatilityMean: m.volatilityMean || undefined,
          volatility_updated_at: m.volatilityUpdatedAt || undefined,
          riskBackTesting: [], // Legacy field
        }));
        
        // Debug: Check if ratio materials have contractType
        const ratioMaterials = materialsForStore.filter(m => m.contractType === 'ratio');
        console.log('[AccountSelector] Ratio materials loaded:', ratioMaterials.map(m => ({ 
          id: m.id, 
          name: m.name, 
          contractType: m.contractType 
        })));
        
        if (mapped.length > 0) {
          console.log('[AccountSelector] Updating formulasToAccount in store', mapped);
          setFormulaToAccount(mapped);
          console.log('[AccountSelector] Updating materialsMap with volatility data', materialsForStore);
          setMaterials(materialsForStore);
        } else {
          console.log('[AccountSelector] No mapped materials to set in store, clearing formulasToAccount');
          setFormulaToAccount([]);
          setMaterials([]);
        }
      } catch (e: any) {
        console.error('Failed to load account materials from API', e);
      }
    };

    loadAccountMaterials();
  }, [sessionUser?.accountId, firebaseReady, setFormulaToAccount]);

  // in Case Of Admin Mode Fetch All accounts
  const {
    data: accountsListData,
    error: accountsDataListError,
    isLoading: isAccountsDataLoading,
  } = useAllAccounts(isAdminMode);

  // Not Admin Mode - Fetch account by user

  //Updates account store
  useEffect(() => {
    setIsAccountLoading(isAccountDataLoading);
  }, [isAccountDataLoading]);

  // Handle Fetching Account List
  useEffect(() => {
    const handleAccounts = async () => {
      if (isAccountsDataLoading || !isAdminMode) return;
      if (!accountsDataListError && accountsListData) {
        idToCompanyNameMap = accountsListData?.reduce(
          (acc, { id }: Account) => ({ ...acc, [id || '']: id || '' }),
          {} as Record<string, string>,
        );
        const companyOptions = Object.entries(idToCompanyNameMap);
        setCompanyNames(companyOptions);
      } else {
        console.error('Error fetching data');
      }
    };
    handleAccounts(); // Getting All The Accounts as Use Accounts
  }, [accountsListData]);

  // No longer need to sync isAdmin to Zustand - derive directly from userData

  // State to hold the selected option (optional, depending on your use case)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const handleSelectAccountChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCompanyId(event.target.value);
    const currentAccount = accountsListData && accountsListData.find((x) => x.id === event.target.value);
    
    if (currentAccount) {
      // Fetch custom terms from Postgres API and merge into account
      try {
        const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await fetch(`${API_URL}/api/v2/accounts`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (response.ok) {
            const data = await response.json();
            const pgAccount = data.accounts?.find((acc: any) => String(acc.id) === String(event.target.value));
            
            if (pgAccount) {
              currentAccount.demandTerm = pgAccount.demandTerm || 'Demand';
              currentAccount.supplyTerm = pgAccount.supplyTerm || 'Supply';
              currentAccount.hedgeTerm = pgAccount.hedgeTerm || 'Hedges';
              currentAccount.viewingHorizonMonths = pgAccount.viewingHorizonMonths || 12;
              currentAccount.baseCurrency = pgAccount.currency || 'USD';
              currentAccount.visiblePolicies = pgAccount.settings?.visiblePolicies || undefined;
              currentAccount.lastImportAt = pgAccount.lastImportAt || null;
              currentAccount.dataFreshnessThresholds = pgAccount.settings?.dataFreshnessThresholds || undefined;
              
              console.log('[AccountSelector] Admin selected account with custom terms:', {
                accountId: event.target.value,
                demandTerm: currentAccount.demandTerm,
                supplyTerm: currentAccount.supplyTerm,
                hedgeTerm: currentAccount.hedgeTerm,
              });
            }
          }
        }
      } catch (error) {
        console.error('[AccountSelector] Failed to fetch custom terms from Postgres:', error);
      }
    }
    
    setAccount([currentAccount as Account, event.target.value]);
  };

  if (!isAdmin) return <></>;

  return (
    <div className="flex space-x-[1rem] mx-[1rem]">
      <ButtonDesigned
        className="hidden"
        style={isAdminMode ? 'primary' : 'outline'}
        onButtonClick={() => setIsAdminMode(!isAdminMode)}
      >
        {isAdminMode ? 'Admin mode' : 'User Mode'}
      </ButtonDesigned>
      {isAdminMode && (
        <select
          className="border rounded p-[0.2rem]"
          title="Company Selector"
          value={selectedCompanyId}
          onChange={handleSelectAccountChange}
        >
          {companyNames.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};
export default AccountSelector;
